import { type FormEvent, type ReactElement, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { errorText } from '../api/client';
import { useLogin, useMe, useSetupState } from '../api/hooks';
import { Brand } from '../components/Brand';
import { PasswordInput } from '../components/PasswordInput';
import { Spinner } from '../components/Spinner';
import { useT } from '../lib/i18n';

/** One class for both fields, so the two never drift apart by a pixel. */
const FIELD =
  'min-h-11 w-full rounded-lg border border-line-strong bg-surface px-3.5 py-2.5 text-base outline-none transition-colors placeholder:text-ink-400 focus:border-accent';

export default function LoginPage(): ReactElement {
  const t = useT();
  const { data: user, isPending } = useMe();
  const { data: setup } = useSetupState();
  const login = useLogin();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const origin = (location.state as { from?: { pathname: string; search?: string } } | null)?.from;
  // The query string is part of the destination: a page reached with one and
  // returned to without it is not the page that was asked for.
  const from = origin ? `${origin.pathname}${origin.search ?? ''}` : '/';

  // Wait for the session before drawing anything: a form that appears and is
  // then replaced by the home page is a worse first second than a spinner.
  if (isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (user) return <Navigate to={from} replace />;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    // A username never contains a space (`USERNAME_PATTERN`), but a mobile
    // keyboard adds one after an autocompletion and pasted text carries them at
    // both ends. Untrimmed, the field looks right while sign-in is refused with
    // no visible reason.
    login.mutate(
      { username: username.trim(), password },
      { onSuccess: () => void navigate(from, { replace: true }) },
    );
  };

  return (
    <div className="flex min-h-full items-center justify-center px-5 py-12">
      <div className="w-full max-w-sm">
        {/* The one screen where the instance introduces itself: whoever arrives
            here has followed a bookmark or a link, and the mark and the name are
            what say they are on their own instance before they type a password
            into it. */}
        <Brand size="lg" className="mb-6" />
        <p className="mb-8 text-ink-500">{t('login.subtitle')}</p>

        {/* Above the form, because it explains why nothing typed into the form
            can work. An instance whose database holds no account answers every
            request and refuses every sign-in — it looks broken when all that is
            missing is one command. */}
        {setup?.needsSetup && (
          <div className="mb-6 rounded-xl border border-line-strong bg-accent-soft px-4 py-3.5 text-sm">
            <p className="font-medium text-ink-900">{t('login.noAccount')}</p>
            <p className="mt-1 text-ink-500">{t('login.createAdmin')}</p>
            <code className="mt-2.5 block overflow-x-auto rounded-lg bg-surface px-3 py-2 font-mono text-xs text-accent">
              pnpm create-admin &lt;username&gt;
            </code>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="username" className="mb-1.5 block text-sm font-medium text-ink-700">
              {t('login.username')}
            </label>
            <input
              id="username"
              name="username"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
              required
              disabled={login.isPending}
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              className={FIELD}
            />
          </div>

          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-ink-700">
              {t('login.password')}
            </label>
            <PasswordInput
              id="password"
              name="password"
              autoComplete="current-password"
              required
              disabled={login.isPending}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className={FIELD}
            />
          </div>

          {/* The region exists whether or not it has something to say: an alert
              inserted into the page along with its text is announced
              inconsistently, whereas one that is already there is announced
              every time it fills. */}
          <div role="alert" aria-live="assertive">
            {login.error && (
              <p className="rounded-lg bg-alert-soft px-3.5 py-2.5 text-sm text-alert">
                {/* The server's own words. It knows why it refused — wrong
                    password, too many attempts and how long to wait — and it has
                    already written it in the language this page asked for. */}
                {errorText(login.error, t('login.failed'))}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={login.isPending}
            className="min-h-11 w-full rounded-lg bg-accent px-3 text-sm font-semibold text-accent-ink transition-colors hover:bg-accent-dim disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t(login.isPending ? 'login.submitting' : 'login.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}
